/**
 * Preview country picker - Markets + curated developed / high-population codes.
 * Full ISO stays in CountryMultiSelect (rules). Shopify has no world-countries API.
 */
import { useMemo } from "react";
import {
  buildPreviewCountryOptions,
  type PreviewCountryGroup,
} from "../lib/preview-country";
import { fieldValue } from "../lib/polaris";

type Props = {
  label?: string;
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  /** Shop market countries (shown first, tagged Primary). */
  prefer?: string[];
  details?: string;
  /** Quick chips above the select (default on). */
  chips?: boolean;
};

function optionLabel(
  name: string,
  group: PreviewCountryGroup,
  primary?: boolean,
): string {
  if (group === "market") {
    return primary ? `${name} · Primary` : `${name} · Market`;
  }
  if (group === "developed") return `${name} · Developed`;
  return `${name} · High population`;
}

export function CountrySelect({
  label = "Where is your shopper?",
  value,
  onChange,
  disabled,
  prefer = [],
  details,
  chips = true,
}: Props) {
  const selected = (value || "").toUpperCase();
  const options = useMemo(
    () => buildPreviewCountryOptions(prefer),
    [prefer.join("|")],
  );

  const chipCodes = useMemo(() => {
    const markets = options.filter((o) => o.group === "market").map((o) => o.code);
    const extras = ["GB", "DE", "IN", "VN", "BR"].filter(
      (c) => !markets.includes(c) && options.some((o) => o.code === c),
    );
    return [...markets, ...extras].slice(0, 8);
  }, [options]);

  return (
    <s-stack direction="block" gap="base">
      {chips && chipCodes.length ? (
        <s-stack direction="inline" gap="small">
          {chipCodes.map((code) => {
            const opt = options.find((o) => o.code === code);
            const active = code === selected;
            return (
              <s-button
                key={code}
                type="button"
                variant={active ? "primary" : "secondary"}
                disabled={disabled}
                onClick={() => onChange(code)}
              >
                {opt?.primary ? `${opt.name} · Primary` : opt?.name || code}
              </s-button>
            );
          })}
        </s-stack>
      ) : null}
      <s-select
        label={label}
        value={
          options.some((o) => o.code === selected)
            ? selected
            : options[0]?.code || ""
        }
        disabled={disabled}
        onChange={(e: Event) =>
          onChange(fieldValue(e).toUpperCase().slice(0, 2))
        }
        details={
          details ||
          "Markets first, then developed and high-population countries for Stay vs ask tests."
        }
      >
        {options.map((c) => (
          <s-option key={c.code} value={c.code}>
            {optionLabel(c.name, c.group, c.primary)}
          </s-option>
        ))}
      </s-select>
    </s-stack>
  );
}
